// 사주 프로필: 원국에서 계산하는 값. 이벤트 조건·직업·소품·결과 문장 생성이 같은 값을 읽는다.
// 규칙은 ai/knowledge/profile-spec.md 와 같다. 완전한 격국 성패·용신·사령 분일 판정이 아니다.
import { HIDDEN_STEMS, STEM_EL, BRANCH_EL, sipseong } from './hanja.js';

export const STEM_NAME = ['갑', '을', '병', '정', '무', '기', '경', '신', '임', '계'];
export const BRANCH_NAME = ['자', '축', '인', '묘', '진', '사', '오', '미', '신', '유', '술', '해'];
export const ELEMENT_NAME = ['목', '화', '토', '금', '수'];

// 일간의 록지(건록): 甲寅 乙卯 丙巳 丁午 戊巳 己午 庚申 辛酉 壬亥 癸子
const LOK_BRANCH = [2, 3, 5, 6, 5, 6, 8, 9, 11, 0];
// 양간의 제왕지(양인): 甲卯 丙午 戊午 庚酉 壬子. 음간은 양인격으로 보지 않는다.
const YANGIN_BRANCH = { 0: 3, 2: 6, 4: 6, 6: 9, 8: 0 };
const GYEOK_BY_SIPSEONG = {
  식신: '식신격', 상관: '상관격', 편재: '편재격', 정재: '정재격',
  편관: '편관격', 정관: '정관격', 편인: '편인격', 정인: '정인격',
};

// 지원 구간은 서버 InitialStatModel 의 기준(0.40 / 0.65)과 같다.
export const SUPPORT_LOW = 0.4;
export const SUPPORT_HIGH = 0.65;

export function seasonOf(branch) {
  if ([2, 3, 4].includes(branch)) return { name: '봄', transition: branch === 4 };
  if ([5, 6, 7].includes(branch)) return { name: '여름', transition: branch === 7 };
  if ([8, 9, 10].includes(branch)) return { name: '가을', transition: branch === 10 };
  return { name: '겨울', transition: branch === 1 }; // 해 자 축
}

/** 월지 본기 기준의 단순 격국. 건록·양인은 록지/제왕지를 먼저 본다. */
export function gyeokOf(dayStem, monthBranch) {
  if (!Number.isInteger(dayStem) || !Number.isInteger(monthBranch)) return null;
  if (LOK_BRANCH[dayStem] === monthBranch) return { name: '건록격', basis: '월지가 일간의 록지' };
  if (YANGIN_BRANCH[dayStem] === monthBranch) return { name: '양인격', basis: '월지가 양간의 제왕지' };
  const main = HIDDEN_STEMS[monthBranch][0];
  const ten = sipseong(dayStem, main);
  if (ten === '비견' || ten === '겁재') return { name: '월겁격', basis: `월지 본기가 ${ten}` };
  return { name: GYEOK_BY_SIPSEONG[ten], basis: `월지 본기가 ${ten}` };
}

// 한난(추움↔따뜻함)·조습(습함↔건조함): 글자마다 -3~+3 값을 주고 자리별 비중으로 합산한 게임용 추정이다.
// 고서에는 이 수치 공식이 없다. 궁통보감·자평진전의 조후론(한난조습) 방향을 따라 정한 표이며 바꿀 수 있다.
//          갑   을   병   정   무   기   경   신   임   계
const STEM_WARM = [0.5, 0.5, 3, 2.5, 1, 0.5, -1, -1.5, -3, -2.5];
const STEM_DRY = [0, -0.5, 2.5, 2, 1.5, -1, 1, -0.5, -3, -2.5];
//          자   축   인   묘   진   사   오   미   신   유   술   해
const BRANCH_WARM = [-3, -3, -1, 0.5, 1, 2.5, 3, 3, 0.5, -1, -0.5, -2];
const BRANCH_DRY = [-3, -2, 0, 0, -1.5, 2, 3, 2.5, 0.5, 1, 3, -2.5];
// 자리별 비중: 월지(계절)가 가장 크다.
const CLIMATE_WEIGHT = {
  hour: { stem: 1, branch: 1 }, day: { stem: 1.5, branch: 1.5 },
  month: { stem: 1.5, branch: 3 }, year: { stem: 1, branch: 1 },
};
const CLIMATE_BAND = [[-35, 0], [-12, 1], [12, 2], [35, 3]]; // 경계값, 구간 번호
const BAND_WARM = ['매우 추움', '추운 편', '중간', '따뜻한 편', '매우 따뜻함'];
const BAND_DRY = ['매우 습함', '습한 편', '중간', '건조한 편', '매우 건조함'];

function bandIndex(score) {
  for (const [limit, index] of CLIMATE_BAND) if (score < limit) return index;
  return 4;
}

// 인접한 글자의 오행 관계: 생(+25%)은 받는 글자의 값을 키우고, 극(-40%)은 줄인다. 고서의 수치가 아니라 게임용 근사이다.
// 목→화→토→금→수→목 순서가 상생, 한 칸 건너뛴 방향이 상극이다.
const GEN_GAIN = 1.25, KE_LOSS = 0.6, MULT_MIN = 0.4, MULT_MAX = 1.6;
const PILLAR_ORDER = ['year', 'month', 'day', 'hour'];

function interactionMultipliers(present) {
  // present: 기둥 이름 → { stemEl, branchEl }. 반환: 'month.stem' 같은 키 → 곱하는 값
  const mult = {};
  const bump = (key, factor) => { mult[key] = Math.min(MULT_MAX, Math.max(MULT_MIN, (mult[key] ?? 1) * factor)); };
  const relate = (aKey, aEl, bKey, bEl) => {
    if (aEl === bEl) return;
    if ((aEl + 1) % 5 === bEl) bump(bKey, GEN_GAIN);       // a 생 b
    else if ((bEl + 1) % 5 === aEl) bump(aKey, GEN_GAIN);  // b 생 a
    else if ((aEl + 2) % 5 === bEl) bump(bKey, KE_LOSS);   // a 극 b
    else bump(aKey, KE_LOSS);                               // b 극 a
  };
  PILLAR_ORDER.forEach((name, i) => {
    const p = present[name];
    if (!p) return;
    relate(`${name}.stem`, p.stemEl, `${name}.branch`, p.branchEl); // 한 기둥의 위아래
    const next = present[PILLAR_ORDER[i + 1]];
    if (!next) return; // 이웃 기둥이 없으면(시주 모름) 건너뛴다
    const nextName = PILLAR_ORDER[i + 1];
    relate(`${name}.stem`, p.stemEl, `${nextName}.stem`, next.stemEl);
    relate(`${name}.branch`, p.branchEl, `${nextName}.branch`, next.branchEl);
  });
  return mult;
}

/**
 * 원국 여덟 글자(시주를 모르면 여섯 글자)에서 한난·조습 점수(-100~100)를 만든다.
 * 월주가 없으면 계산하지 않는다. interactions: false 면 인접 글자의 생극을 반영하지 않는다.
 */
export function climateOf(saju, { interactions = true } = {}) {
  const present = {};
  for (const key of PILLAR_ORDER) {
    const p = saju?.[key];
    if (p && Number.isInteger(p.stem) && Number.isInteger(p.branch)) {
      present[key] = { ...p, stemEl: STEM_EL[p.stem], branchEl: BRANCH_EL[p.branch] };
    }
  }
  if (!present.month) return null;
  const mult = interactions ? interactionMultipliers(present) : {};
  let warm = 0, dry = 0, weight = 0;
  for (const [key, w] of Object.entries(CLIMATE_WEIGHT)) {
    const p = present[key];
    if (!p) continue;
    const ms = mult[`${key}.stem`] ?? 1, mb = mult[`${key}.branch`] ?? 1;
    warm += w.stem * STEM_WARM[p.stem] * ms + w.branch * BRANCH_WARM[p.branch] * mb;
    dry += w.stem * STEM_DRY[p.stem] * ms + w.branch * BRANCH_DRY[p.branch] * mb;
    weight += w.stem + w.branch;
  }
  const norm = (v) => Math.max(-100, Math.min(100, Math.round((v / (weight * 3)) * 1000) / 10));
  const w = norm(warm), d = norm(dry);
  return {
    warm: { score: w, label: BAND_WARM[bandIndex(w)], low: '추움', high: '따뜻함' },
    dry: { score: d, label: BAND_DRY[bandIndex(d)], low: '습함', high: '건조함' },
    hourIncluded: Boolean(present.hour),
    interactions,
    scope: interactions ? '글자별 값 합산 + 인접 글자의 생극 반영(게임용 추정)' : '글자별 값 합산(게임용 추정)',
  };
}

/**
 * 자동 결정에만 쓰는 기질 기울기(게임 해석이며 고서의 성격 풀이가 아니다).
 * 따뜻할수록 실행(act)쪽, 추울수록 하지 않기(decline)쪽으로 기운다.
 * 건조할수록 우회(alternative)를 덜 고르고, 습할수록 더 고른다. weight 는 기질 점수(오행 %)와 같은 단위.
 */
export function climateTilt(climate, weight = 0) {
  if (!climate || !weight) return { act: 0, decline: 0, alternative: 0 };
  const warm = (climate.warm.score / 100) * weight;
  const dry = (climate.dry.score / 100) * weight;
  return { act: warm, decline: -warm, alternative: -dry };
}

/** 조후 필요 오행: 한난을 먼저 보고, 한난이 중간이면 조습을 본다. 추움·습함은 화, 따뜻함·건조함은 수가 필요하다. */
export function johuFromClimate(climate) {
  const none = { need: '없음', level: null, scope: '한난조습 합산(게임용 추정)' };
  if (!climate) return none;
  const { warm, dry } = climate;
  const pick = (value, lowNeed, highNeed) => (value <= -12 ? lowNeed : value >= 12 ? highNeed : null);
  const byWarm = pick(warm.score, '화', '수');
  const need = byWarm ?? pick(dry.score, '화', '수');
  if (!need) return none;
  const strength = byWarm ? Math.abs(warm.score) : Math.abs(dry.score);
  return { need, level: strength >= 35 ? '높음' : '보통', scope: none.scope };
}

export function supportBand(index) {
  if (!Number.isFinite(index)) return null;
  return index < SUPPORT_LOW ? '적음' : index > SUPPORT_HIGH ? '많음' : '중간';
}

/** 원국(서버 응답)에서 프로필을 만든다. 시주가 없어도 계산된다. */
export function sajuProfile(saju) {
  const dayStem = saju?.day?.stem, monthBranch = saju?.month?.branch;
  if (!Number.isInteger(dayStem) || !Number.isInteger(monthBranch)) return null;
  const season = seasonOf(monthBranch);
  const index = saju.statBasis?.daySupportIndex;
  const climate = climateOf(saju);
  return {
    dayStem: STEM_NAME[dayStem],
    dayElement: ELEMENT_NAME[STEM_EL[dayStem]],
    polarity: dayStem % 2 === 0 ? '양' : '음',
    monthBranch: BRANCH_NAME[monthBranch],
    season: season.name,
    transition: season.transition,
    gyeok: gyeokOf(dayStem, monthBranch),
    climate,
    johu: johuFromClimate(climate),
    support: { index: Number.isFinite(index) ? index : null, band: supportBand(index) },
    limits: ['격국은 월지 본기 기준의 단순 판정이며 성패·용신·사령 분일은 판정하지 않는다.',
      '한난·조습은 글자별 값 합산과 인접 글자의 생극을 반영한 게임용 추정이며 궁통보감의 일간별 월별 표·합충·지장간은 반영하지 않는다.'],
  };
}

// 이벤트 조건용 값. 알 수 없으면 null (조건은 거짓이 된다).
export function profileMetric(saju, metric) {
  const profile = sajuProfile(saju);
  if (!profile) return null;
  switch (metric) {
    case 'dayStem': return profile.dayStem;
    case 'dayElement': return profile.dayElement;
    case 'season': return profile.season;
    case 'gyeok': return profile.gyeok?.name ?? null;
    case 'supportBand': return profile.support.band;
    default: return null;
  }
}
// 한난조습(climate)과 그에서 나온 조후 필요 오행은 이벤트 조건으로 쓰지 않는다. 자동 결정(climateTilt)에만 반영한다.
export const PROFILE_METRICS = ['dayStem', 'dayElement', 'season', 'gyeok', 'supportBand'];
